import { useRef } from "react";
import Stars from "@/components/base/Stars";

export interface Review {
  id: string;
  name: string;
  location: string;
  avatar: string;
  quote: string;
  date?: string;
  source: string;
  rating?: number;
}

interface ReviewsCarouselProps {
  reviews: Review[];
}

const SOURCE_META: Record<string, { label: string; icon: string }> = {
  google: { label: "Google", icon: "ri-google-fill" },
  tripadvisor: { label: "TripAdvisor", icon: "ri-chat-quote-line" },
};

export default function ReviewsCarousel({ reviews }: ReviewsCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollByCard = (direction: number) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-review-card]");
    const amount = card ? card.offsetWidth + 12 : el.clientWidth * 0.5;
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div className="flex md:hidden items-center justify-center gap-1.5 mb-3 text-[11px] font-medium text-foreground-500">
        <i className="ri-drag-move-2-line text-sm" />
        Deslize para ver mais avaliações
      </div>

      <div
        ref={trackRef}
        className="flex gap-3 lg:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {reviews.map((review) => {
          const source = SOURCE_META[review.source] ?? SOURCE_META.google;
          const ratingLabel = (review.rating ?? 5).toFixed(1).replace(".", ",");

          return (
            <article
              key={review.id}
              data-review-card
              className="snap-start flex-shrink-0 w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-2rem)/3)] bg-background-50 rounded-lg p-3 md:p-4 flex flex-col border border-background-200/70 hover:border-accent-300 transition-colors duration-200"
            >
              <div className="flex items-center gap-2 md:gap-2.5 mb-2">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-accent-100 bg-secondary-100 flex items-center justify-center">
                  {review.avatar ? (
                    <img
                      src={review.avatar}
                      alt={`Foto de ${review.name}`}
                      title={`${review.name} - Avaliação 5 estrelas`}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <i className="ri-user-3-line text-secondary-700 text-base md:text-lg" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs md:text-sm font-heading font-bold text-foreground-900 truncate">
                    {review.name}
                  </p>
                  <p className="text-[10px] md:text-[11px] text-foreground-500 truncate">
                    {review.location}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5 mb-1.5">
                <Stars className="text-sm md:text-base" />
                <span className="text-[10px] md:text-[11px] font-semibold text-foreground-600">
                  {ratingLabel}
                </span>
              </div>

              <p className="text-[11px] md:text-xs text-foreground-700 leading-relaxed flex-1 line-clamp-5 md:line-clamp-4">
                “{review.quote}”
              </p>

              <div className="flex items-center justify-between gap-2 mt-2.5 pt-2.5 border-t border-background-200/70">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-foreground-500 whitespace-nowrap">
                  <i className={`${source.icon} text-sm text-secondary-600`} />
                  {source.label}
                </span>
                {review.date && (
                  <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                    {review.date}
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {reviews.length > 3 && (
        <div className="flex items-center justify-center gap-3 mt-5">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="Avaliação anterior"
            className="w-10 h-10 rounded-full border border-background-300/70 bg-background-50 flex items-center justify-center text-primary-600 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="Próxima avaliação"
            className="w-10 h-10 rounded-full border border-background-300/70 bg-background-50 flex items-center justify-center text-primary-600 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      )}
    </div>
  );
}